/**
 * Validation Rules for Business Entities
 * Contains regex patterns and validation functions for various field types
 */

// Regex patterns for field validation
export const validationPatterns = {
  // SSN format: XXX-XX-XXXX or XXXXXXXXX
  SSN: /^(?:\d{3}-\d{2}-\d{4}|\d{9})$/,

  // ITIN format: 9XX-XX-XXXX (starts with 9, formatted like SSN)
  ITIN: /^(?:9\d{2}-\d{2}-\d{4}|9\d{8})$/,

  // SSN or ITIN combined
  SSNOrITIN: /^(?:\d{3}-\d{2}-\d{4}|\d{9}|9\d{2}-\d{2}-\d{4}|9\d{8})$/,

  // EIN format: XX-XXXXXXX or XXXXXXXXX
  EIN: /^(?:\d{2}-\d{7}|\d{9})$/,

  // GIIN format: XXXXXX.XXXXX.XX.XXX — 3rd segment (chars 14-15) must be a valid entity type code
  // Valid entity type codes: LE, SL, ME, BR, SP, SF, SD, SS, SB
  GIIN: /^[A-Z0-9]{6}\.[A-Z0-9]{5}\.(LE|SL|ME|BR|SP|SF|SD|SS|SB)\.[A-Z0-9]{3}$/i,

  // FATCA ID: first 6 characters of a GIIN — alphanumeric, letter 'O' is never used
  FATCA_ID: /^[A-NP-Z0-9]{6}$/i,

  // ATIN format: XXX-XX-XXXX (similar to SSN)
  ATIN: /^(?:\d{3}-\d{2}-\d{4}|\d{9})$/,

  // PTIN format: PXXXXXXXX (P followed by 8 digits)
  PTIN: /^P\d{8}$/,

  // QIEIN format: XX-XXXXXXX (similar to EIN)
  QIEIN: /^(?:\d{2}-\d{7}|\d{9})$/,

  // WP-EIN format: XX-XXXXXXX (similar to EIN)
  WPEIN: /^(?:\d{2}-\d{7}|\d{9})$/,

  // WT-EIN format: XX-XXXXXXX (similar to EIN)
  WTEIN: /^(?:\d{2}-\d{7}|\d{9})$/,

  // Date format: MM/DD/YYYY
  Date: /^(?:0[1-9]|1[0-2])\/(?:0[1-9]|[12]\d|3[01])\/\d{4}$/,

  // Account Connections template date format: MM-DD-YYYY
  DateHyphen: /^(?:0[1-9]|1[0-2])-(?:0[1-9]|[12]\d|3[01])-\d{4}$/,

  // Email format
  Email: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,

  // Phone number formats (various)
  Phone: /^[\d\s\-\(\)\+]+$/,

  // Zip/Postal Code (US and general)
  ZipCode: /^(?:\d{5}(?:-\d{4})?|[A-Z]\d[A-Z]\s?\d[A-Z]\d)$/,

  // Percentage format: XX.XX or XX
  Percentage: /^\d+(?:\.\d{1,2})?$/,

  // Withholding Rate: 0-100 with up to 2 decimal places
  WithholdingRate: /^(?:100(?:\.0{1,2})?|[1-9]?\d(?:\.\d{1,2})?)$/,
};

// Field validation rules mapping
export const fieldValidationRules = {
  SSNOrITIN: {
    pattern: validationPatterns.SSNOrITIN,
    errorMessage: "Invalid SSN/ITIN format. Expected: XXX-XX-XXXX or XXXXXXXXX",
    allowEmpty: true,
  },
  EIN: {
    pattern: validationPatterns.EIN,
    errorMessage: "Invalid EIN format. Expected: XX-XXXXXXX or XXXXXXXXX",
    allowEmpty: true,
  },
  GIIN: {
    pattern: validationPatterns.GIIN,
    errorMessage:
      "Invalid GIIN format. Expected: XXXXXX.XXXXX.XX.XXX where characters 14-15 must be one of: LE, SL, ME, BR, SP, SF, SD, SS, SB",
    allowEmpty: true,
  },
  "FATCA ID": {
    pattern: validationPatterns.FATCA_ID,
    errorMessage:
      "Invalid FATCA ID. Must be exactly 6 alphanumeric characters. The letter 'O' is never used in a FATCA ID.",
    allowEmpty: true,
  },
  ATIN: {
    pattern: validationPatterns.ATIN,
    errorMessage: "Invalid ATIN format. Expected: XXX-XX-XXXX or XXXXXXXXX",
    allowEmpty: true,
  },
  ITIN: {
    pattern: validationPatterns.ITIN,
    errorMessage: "Invalid ITIN format. Expected: 9XX-XX-XXXX",
    allowEmpty: true,
  },
  PTIN: {
    pattern: validationPatterns.PTIN,
    errorMessage: "Invalid PTIN format. Expected: PXXXXXXXX",
    allowEmpty: true,
  },
  QIEIN: {
    pattern: validationPatterns.QIEIN,
    errorMessage: "Invalid QIEIN format. Expected: XX-XXXXXXX or XXXXXXXXX",
    allowEmpty: true,
  },
  "WP-EIN": {
    pattern: validationPatterns.WPEIN,
    errorMessage: "Invalid WP-EIN format. Expected: XX-XXXXXXX or XXXXXXXXX",
    allowEmpty: true,
  },
  "WT-EIN": {
    pattern: validationPatterns.WTEIN,
    errorMessage: "Invalid WT-EIN format. Expected: XX-XXXXXXX or XXXXXXXXX",
    allowEmpty: true,
  },
  "Date of Birth(MM/DD/YYYY)": {
    pattern: validationPatterns.Date,
    errorMessage: "Invalid date format. Expected: MM/DD/YYYY",
    allowEmpty: true,
  },
  "Date of Incorporation": {
    pattern: validationPatterns.Date,
    errorMessage: "Invalid date format. Expected: MM/DD/YYYY",
    allowEmpty: true,
  },
  "TIN Check Date(MM/DD/YYYY)": {
    pattern: validationPatterns.Date,
    errorMessage: "Invalid date format. Expected: MM/DD/YYYY",
    allowEmpty: true,
  },
  "Previous Name Date": {
    pattern: validationPatterns.Date,
    errorMessage: "Invalid date format. Expected: MM/DD/YYYY",
    allowEmpty: true,
  },
  DeceasedOn: {
    pattern: validationPatterns.Date,
    errorMessage: "Invalid date format. Expected: MM/DD/YYYY",
    allowEmpty: true,
  },
  "Signed Certification Confirmation Date": {
    pattern: validationPatterns.Date,
    errorMessage: "Invalid date format. Expected: MM/DD/YYYY",
    allowEmpty: true,
  },
  "VAT Check Date (MM/DD/YYYY)": {
    pattern: validationPatterns.Date,
    errorMessage: "Invalid date format. Expected: MM/DD/YYYY",
    allowEmpty: true,
  },
  VATCheckDate: {
    pattern: validationPatterns.Date,
    errorMessage: "Invalid date format. Expected: MM/DD/YYYY",
    allowEmpty: true,
  },
  "Date MM-DD-YYYY": {
    pattern: validationPatterns.DateHyphen,
    errorMessage: "Invalid date format. Expected: MM-DD-YYYY",
    allowEmpty: true,
  },
  OpenedOn: {
    pattern: validationPatterns.Date,
    errorMessage: "Invalid date format. Expected: MM/DD/YYYY",
    allowEmpty: true,
  },
  ClosedOn: {
    pattern: validationPatterns.Date,
    errorMessage: "Invalid date format. Expected: MM/DD/YYYY",
    allowEmpty: true,
  },
  "Contact Email": {
    pattern: validationPatterns.Email,
    errorMessage: "Invalid email format",
    allowEmpty: true,
  },
  "Contact Email2": {
    pattern: validationPatterns.Email,
    errorMessage: "Invalid email format",
    allowEmpty: true,
  },
  "Contact Email3": {
    pattern: validationPatterns.Email,
    errorMessage: "Invalid email format",
    allowEmpty: true,
  },
  "Manual Withholding Rate (%) (Format: 00.00)": {
    pattern: validationPatterns.WithholdingRate,
    errorMessage:
      "Invalid withholding rate. Value must be between 0 and 100 (e.g., 15.00, 30, 100)",
    allowEmpty: true,
  },
  // "Zip_PostalCode": {
  //   pattern: validationPatterns.ZipCode,
  //   errorMessage: "Invalid zip/postal code format",
  //   allowEmpty: true,
  // },
  // "Mail Zip_PostalCode": {
  //   pattern: validationPatterns.ZipCode,
  //   errorMessage: "Invalid zip/postal code format",
  //   allowEmpty: true,
  // },
};

/**
 * Detect TIN type from value format for Tax Identification Number field
 * @param {string} value - The TIN value
 * @returns {string} - Detected type (SSN, EIN, ITIN)
 */
const detectTINType = (value) => {
  if (!value || typeof value !== "string") return null;

  const cleanValue = value.trim();

  // ITIN: 9XX-XX-XXXX (check first since it's more specific than SSN)
  if (/^9\d{2}-\d{2}-\d{4}$/.test(cleanValue) || /^9\d{8}$/.test(cleanValue)) {
    return "ITIN";
  }

  // SSN: XXX-XX-XXXX
  if (/^\d{3}-\d{2}-\d{4}$/.test(cleanValue) || /^\d{9}$/.test(cleanValue)) {
    return "SSN";
  }

  // EIN: XX-XXXXXXX
  if (/^\d{2}-\d{7}$/.test(cleanValue)) {
    return "EIN";
  }

  return null;
};

/**
 * Validate a field value against its defined rule
 * @param {string} fieldName - The name of the field
 * @param {string} value - The value to validate
 * @returns {object} - { isValid: boolean, errorMessage: string }
 */
// Valid GIIN entity type codes (characters 14-15)
const VALID_GIIN_ENTITY_TYPE_CODES = ["LE", "SL", "ME", "BR", "SP", "SF", "SD", "SS", "SB"];

export const validateField = (fieldName, value) => {
  // Special handling for GIIN — give distinct errors for format vs entity type code vs FATCA ID
  if (fieldName === "GIIN") {
    if (!value || value.trim() === "") {
      return { isValid: true, errorMessage: "" };
    }
    const cleanValue = value.trim().toUpperCase();
    const basicFormat = /^[A-Z0-9]{6}\.[A-Z0-9]{5}\.[A-Z]{2}\.[A-Z0-9]{3}$/.test(cleanValue);
    if (!basicFormat) {
      return {
        isValid: false,
        errorMessage: "Invalid GIIN format. Expected: XXXXXX.XXXXX.XX.XXX",
      };
    }
    const fatcaId = cleanValue.split(".")[0];
    if (/O/.test(fatcaId)) {
      return {
        isValid: false,
        errorMessage: `Invalid GIIN: FATCA ID (first 6 characters '${fatcaId}') must not contain the letter 'O'.`,
      };
    }
    const entityTypeCode = cleanValue.split(".")[2];
    if (!VALID_GIIN_ENTITY_TYPE_CODES.includes(entityTypeCode)) {
      return {
        isValid: false,
        errorMessage: `Invalid GIIN entity type code '${entityTypeCode}' (characters 14-15). Must be one of: LE, SL, ME, BR, SP, SF, SD, SS, SB`,
      };
    }
    return { isValid: true, errorMessage: "" };
  }

  // Special handling for standalone FATCA ID field
  if (fieldName === "FATCA ID") {
    if (!value || value.trim() === "") {
      return { isValid: true, errorMessage: "" };
    }
    const cleanValue = value.trim().toUpperCase();
    if (cleanValue.length !== 6) {
      return {
        isValid: false,
        errorMessage: `Invalid FATCA ID: must be exactly 6 characters (got ${cleanValue.length}).`,
      };
    }
    if (/O/.test(cleanValue)) {
      return {
        isValid: false,
        errorMessage: `Invalid FATCA ID '${cleanValue}': the letter 'O' is never used in a FATCA ID.`,
      };
    }
    if (!/^[A-Z0-9]{6}$/.test(cleanValue)) {
      return {
        isValid: false,
        errorMessage:
          "Invalid FATCA ID: must contain only alphanumeric characters (A-Z, 0-9, excluding 'O').",
      };
    }
    return { isValid: true, errorMessage: "" };
  }

  // Special handling for "Tax Identification Number" field in Forms tab
  // Only validate EIN and SSN/ITIN formats
  if (fieldName === "Tax Identification Number") {
    if (!value || value.trim() === "") {
      return { isValid: true, errorMessage: "" };
    }

    const detectedType = detectTINType(value);

    if (detectedType === "EIN") {
      const isValid = validationPatterns.EIN.test(value);
      return {
        isValid,
        errorMessage: isValid ? "" : "Invalid EIN format. Expected: XX-XXXXXXX",
      };
    } else if (detectedType === "SSN") {
      const isValid = validationPatterns.SSN.test(value);
      return {
        isValid,
        errorMessage: isValid ? "" : "Invalid SSN format. Expected: XXX-XX-XXXX",
      };
    } else if (detectedType === "ITIN") {
      const isValid = validationPatterns.ITIN.test(value);
      return {
        isValid,
        errorMessage: isValid ? "" : "Invalid ITIN format. Expected: 9XX-XX-XXXX",
      };
    } else {
      // Unknown format
      return {
        isValid: false,
        errorMessage:
          "Invalid TIN format. Expected: EIN (XX-XXXXXXX), SSN (XXX-XX-XXXX), or ITIN (9XX-XX-XXXX)",
      };
    }
  }

  const rule = fieldValidationRules[fieldName];

  // If no rule exists, consider it valid
  if (!rule) {
    return { isValid: true, errorMessage: "" };
  }

  // Allow empty values if configured
  if (rule.allowEmpty && (!value || value.trim() === "")) {
    return { isValid: true, errorMessage: "" };
  }

  // Special validation for Manual Withholding Rate - check if value > 100
  if (fieldName === "Manual Withholding Rate (%) (Format: 00.00)") {
    const numValue = parseFloat(value);
    if (!isNaN(numValue) && numValue > 100) {
      return {
        isValid: false,
        errorMessage: "Withholding rate cannot exceed 100%",
      };
    }
  }

  // Test against pattern
  const isValid = rule.pattern.test(value);

  return {
    isValid,
    errorMessage: isValid ? "" : rule.errorMessage,
  };
};

/**
 * Validate an entire row of Business Entities data
 * @param {object} row - The row data object
 * @param {number} rowIndex - The index of the row
 * @returns {array} - Array of error objects
 */
export const validateBusinessEntityRow = (row, rowIndex) => {
  const errors = [];

  Object.keys(fieldValidationRules).forEach((fieldName) => {
    if (row.hasOwnProperty(fieldName)) {
      const value = row[fieldName];
      const validation = validateField(fieldName, value);

      if (!validation.isValid) {
        errors.push({
          rowIndex,
          fieldName,
          value,
          errorMessage: validation.errorMessage,
          uniqueIdentifier: row["UniqueIdentifier"] || row["Unique Identifier"] || "",
        });
      }
    }
  });

  return errors;
};

/**
 * Validate multiple rows
 * @param {array} rows - Array of row data
 * @returns {array} - Array of all errors found
 */
export const validateAllRows = (rows) => {
  const allErrors = [];

  rows.forEach((row, index) => {
    const rowErrors = validateBusinessEntityRow(row, index);
    allErrors.push(...rowErrors);
  });

  return allErrors;
};
